<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores'
import { useRewardsStore } from '@/stores/rewards'
import { useNavigation } from '@/composables/useNavigation'
import { getAvatarUrl } from '@/utils/imageUrl'
import AccountSwitcher from './AccountSwitcher.vue'
import FbKidAvatar from './ui/FbKidAvatar.vue'
import FbStarChip from './ui/FbStarChip.vue'
import NavProfileChip from './NavProfileChip.vue'

withDefaults(defineProps<{ rail?: boolean }>(), { rail: false })

const authStore = useAuthStore()
const rewards = useRewardsStore()
const { navItems, isActive } = useNavigation()
const avatarUrl = computed(() => getAvatarUrl(authStore.user?.avatar_key))
</script>

<template>
  <v-navigation-drawer
    permanent
    :rail="rail"
    :width="264"
    :rail-width="96"
    data-testid="nav-drawer"
    class="fb-nav-drawer"
    aria-label="Main navigation"
  >
    <v-sheet data-testid="nav-logo" class="fb-nav-logo font-display" color="transparent">
      FamBam
    </v-sheet>

    <AccountSwitcher
      v-if="authStore.isAdult && authStore.hasMultipleAccounts && !rail"
      testid="nav-account-switcher"
    />

    <v-sheet
      v-if="authStore.currentAccountRole === 'minor'"
      data-testid="nav-kid-header"
      class="fb-nav-kid-header d-flex align-center ga-3"
      :class="{ 'flex-column': rail }"
    >
      <FbKidAvatar
        :name="authStore.user?.name ?? ''"
        :user-id="authStore.user?.id ?? ''"
        :image-url="avatarUrl"
        :size="44"
      />
      <FbStarChip
        v-if="typeof rewards.pointBalance === 'number'"
        :count="rewards.pointBalance"
        size="sm"
      />
      <v-skeleton-loader v-else-if="rewards.isLoading" type="chip" />
      <v-btn
        v-else
        icon="mdi-refresh"
        variant="text"
        :width="44"
        :height="44"
        data-testid="nav-balance-retry"
        aria-label="Retry loading stars"
        @click="rewards.fetchPointBalance()"
      />
    </v-sheet>

    <v-list nav role="group" class="fb-nav-list pa-0" bg-color="transparent">
      <v-list-item
        v-for="item in navItems"
        :key="item.id"
        :to="item.path"
        :data-testid="`nav-${item.id}`"
        :aria-label="item.ariaLabel"
        :aria-current="isActive(item.path) ? 'page' : undefined"
        :active="isActive(item.path)"
        variant="text"
        class="fb-nav-item"
      >
        <v-sheet
          color="transparent"
          class="d-flex align-center"
          :class="rail ? 'flex-column ga-1 text-center' : 'ga-3'"
        >
          <v-badge
            :model-value="!!item.badge"
            :content="item.badge"
            :transition="false"
            color="secondary"
            text-color="on-secondary"
            aria-hidden="true"
          >
            <v-icon :icon="item.icon" :size="22" />
          </v-badge>
          <v-sheet tag="span" color="transparent" class="fb-nav-label">
            {{ item.label }}
          </v-sheet>
        </v-sheet>
      </v-list-item>
    </v-list>

    <template #append>
      <NavProfileChip :rail="rail" />
    </template>
  </v-navigation-drawer>
</template>

<style scoped>
.fb-nav-drawer {
  margin: 16px;
  height: calc(100% - 32px) !important;
  max-height: calc(100dvh - 32px);
  padding-top: var(--safe-area-inset-top, 0px);
  border: 2px solid rgb(var(--v-theme-outline));
  border-radius: 24px;
  overflow: hidden;
  transition: none;
}

.fb-nav-drawer :deep(.v-navigation-drawer__content) {
  min-height: 0;
  padding: 20px 14px 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.fb-nav-drawer :deep(.v-navigation-drawer__append) {
  padding: 14px;
}

.fb-nav-logo {
  margin-bottom: 18px;
  padding-inline: 8px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.fb-nav-kid-header {
  margin-block: 18px;
  padding: 14px;
  border-radius: 18px;
  background: var(--fb-status-waiting-bg);
  color: var(--fb-kid-ink);
}

.fb-nav-item {
  min-height: 48px;
  padding-inline: 14px;
  border-radius: 14px;
}

.fb-nav-label {
  font-size: 16px;
  font-weight: 800;
}

.fb-nav-item .v-sheet {
  color: inherit;
}

.fb-nav-item[aria-current='page'] {
  background: rgb(var(--v-theme-primary-lighten-5));
  color: rgb(var(--v-theme-primary));
}

.v-navigation-drawer--rail :deep(.v-navigation-drawer__content) {
  padding: 16px 8px 0;
}

.v-navigation-drawer--rail :deep(.v-navigation-drawer__append) {
  padding: 8px;
}

.v-navigation-drawer--rail .fb-nav-logo {
  padding: 0;
  font-size: 16px;
  text-align: center;
}

.v-navigation-drawer--rail .fb-nav-item {
  padding: 6px 0;
}

.v-navigation-drawer--rail .fb-nav-kid-header {
  padding-inline: 0;
}

.v-navigation-drawer--rail .fb-nav-label {
  font-size: 12px;
  line-height: 1.2;
}
</style>
